import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { useConfirm } from '@shared/components/ConfirmDialog';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ipAllowlistApi } from '@api/ipAllowlist';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { IpAllowlistFormModal } from '../components/IpAllowlistFormModal';
import type { IpAllowlistEntry, IpAllowlistInput } from '../types';

const { Text } = Typography;

export function IpAllowlistPage() {
  const { t } = useTranslation(['ip-allowlist', 'common']);
  const qc = useQueryClient();
  const confirm = useConfirm();
  const canManage = useCan(PERMISSIONS.SECURITY_MANAGE);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IpAllowlistEntry | null>(null);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['ip-allowlist'],
    queryFn: () => ipAllowlistApi.list(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ip-allowlist'] });

  const createMutation = useMutation({
    mutationFn: (input: IpAllowlistInput) => ipAllowlistApi.create(input),
    onSuccess: async () => {
      toast.success(t('toast.created'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: IpAllowlistInput }) =>
      ipAllowlistApi.update(vars.id, vars.input),
    onSuccess: async () => {
      toast.success(t('toast.updated'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ipAllowlistApi.remove(id),
    onSuccess: async () => {
      toast.success(t('toast.deleted'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (entry: IpAllowlistEntry) => {
    setEditing(entry);
    setModalOpen(true);
  };
  const requestDelete = (entry: IpAllowlistEntry) => {
    void (async () => {
      const ok = await confirm({
        title: t('delete.title'),
        description: t('delete.confirm', { cidr: entry.cidr, label: entry.label }),
        okText: t('delete.ok'),
      });
      if (ok) deleteMutation.mutate(entry.id);
    })();
  };

  const columns: ColumnsType<IpAllowlistEntry> = [
    {
      title: t('columns.cidr'),
      dataIndex: 'cidr',
      key: 'cidr',
      render: (cidr: string) => <Text code>{cidr}</Text>,
    },
    { title: t('columns.label'), dataIndex: 'label', key: 'label' },
    {
      title: t('columns.enabled'),
      key: 'enabled',
      render: (_, r) =>
        r.enabled ? (
          <Tag color="green">{t('common:states.active')}</Tag>
        ) : (
          <Tag>{t('common:states.inactive')}</Tag>
        ),
    },
    {
      title: t('columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => openEdit(r)}
            disabled={!canManage}
            aria-label={t('form.editTitle')}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<Trash2 size={15} />}
            onClick={() => requestDelete(r)}
            disabled={!canManage}
            loading={deleteMutation.isPending && deleteMutation.variables === r.id}
            aria-label={t('delete.title')}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('title') }]}
        actions={
          canManage ? (
            <Button type="primary" icon={<Plus size={16} />} onClick={openCreate}>
              {t('new')}
            </Button>
          ) : null
        }
      />

      <Alert
        type="warning"
        showIcon
        message={t('lockoutWarning')}
        style={{ marginBottom: 16 }}
      />

      <Card>
        <Table<IpAllowlistEntry>
          rowKey="id"
          columns={columns}
          dataSource={entries}
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: t('empty') }}
        />
      </Card>

      <IpAllowlistFormModal
        open={modalOpen}
        editing={editing}
        saving={createMutation.isPending || updateMutation.isPending}
        onCancel={() => setModalOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
        onUpdate={(id, input) => updateMutation.mutate({ id, input })}
      />
    </div>
  );
}
